import React, { useState, useEffect, useRef } from 'react';
import './Dropdown.css';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';

export interface DropdownItem {
  id: string;
  label: string;
  value: string;
}

export interface DropdownSection {
  id: string;
  header?: React.ReactNode;
  items: DropdownItem[];
}

interface DropdownProps {
  items: DropdownItem[] | DropdownSection[];
  selectedId?: string;
  position?: 'left' | 'right';
  onSelect: (value: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  hasSections?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  selectedId,
  position = 'left',
  onSelect,
  label,
  className = '',
  disabled = false,
  hasSections = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // When closing, wait for animation to complete before removing from DOM
      const menu = menuRef.current;
      if (menu) {
        menu.style.animation = 'dropdownClose 0.12s ease-in forwards';
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 120); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  const findSelectedItem = (items: DropdownItem[] | DropdownSection[]): DropdownItem | undefined => {
    if (!hasSections) {
      return (items as DropdownItem[]).find(item => item.id === selectedId);
    }
    
    for (const section of items as DropdownSection[]) {
      const found = section.items.find(item => item.id === selectedId);
      if (found) return found;
    }
    return undefined;
  };

  const selectedItem = findSelectedItem(items);

  const renderItems = (items: DropdownItem[]) => {
    return items.map((item) => (
      <button
        key={item.id}
        className={`dropdown-item ${item.id === selectedId ? 'selected' : ''}`}
        onClick={() => handleSelect(item.value)}
      >
        <span>{item.label}</span>
        {item.id === selectedId && (
          <svg
            className="check-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3332 4L5.99984 11.3333L2.6665 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    ));
  };

  return (
    <div className={`dropdown-container ${className}`}>
      {label && <label className="dropdown-label">{label}</label>}
      <button
        ref={buttonRef}
        className={`dropdown-trigger ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        type="button"
      >
        <span>{selectedItem?.label || 'Select...'}</span>
        <img
          src={dropdownIcon}
          alt=""
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
        />
      </button>
      {isVisible && (
        <div
          ref={menuRef}
          className={`dropdown-menu ${position === 'right' ? 'dropdown-menu-right' : ''}`}
        >
          <div className="dropdown-content">
            {hasSections ? (
              (items as DropdownSection[]).map((section) => (
                <div key={section.id} className="dropdown-section">
                  {section.header && (
                    <div className="dropdown-section-header">
                      {section.header}
                    </div>
                  )}
                  {renderItems(section.items)}
                </div>
              ))
            ) : (
              renderItems(items as DropdownItem[])
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown; 